import React, { useEffect, useState, useRef } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, X, Upload, Download, FileSpreadsheet, Trash2, Pencil, Receipt, Search } from "lucide-react";

const CATEGORY_LABELS = {
  rent: "Rent", electricity: "Electricity", water: "Water", internet: "Internet",
  groceries: "Groceries", stationary: "Stationary", software: "Software",
  travel: "Travel", maintenance: "Maintenance", salary: "Salary", taxes: "Taxes",
  marketing: "Marketing", fuel: "Fuel", office_supplies: "Office Supplies",
  professional_fees: "Professional Fees", other: "Other",
};

const CATEGORY_COLORS = {
  rent: "bg-blue-200", electricity: "bg-yellow-200", water: "bg-cyan-200",
  internet: "bg-purple-200", groceries: "bg-green-200", stationary: "bg-zinc-200",
  software: "bg-indigo-200", travel: "bg-orange-200", maintenance: "bg-amber-200",
  salary: "bg-pink-200", taxes: "bg-red-200", marketing: "bg-rose-200",
  fuel: "bg-emerald-200", office_supplies: "bg-violet-200",
  professional_fees: "bg-teal-200", other: "bg-zinc-100",
};

export default function Expenses() {
  const [expenses, setExpenses] = useState([]);
  const [stats, setStats] = useState({ categories: [], total_inr: 0, month: "" });
  const [filters, setFilters] = useState({ start: "", end: "", category: "", q: "" });
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState(null);
  const fileRef = useRef(null);

  const load = async () => {
    const params = {};
    Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
    const [a, b] = await Promise.all([
      api.get("/expenses", { params }),
      api.get("/expenses/stats/monthly"),
    ]);
    setExpenses(a.data);
    setStats(b.data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filters]);

  const remove = async (id) => {
    if (!confirm("Delete expense?")) return;
    await api.delete(`/expenses/${id}`);
    toast.success("Deleted");
    load();
  };

  const onImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post("/expenses/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Imported ${data.imported} expenses`);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Import failed"); }
    e.target.value = "";
  };

  const downloadFile = async (url, name) => {
    const token = localStorage.getItem("session_token");
    const res = await api.get(url, { params: token ? { auth: token } : {}, responseType: "blob" });
    const u = URL.createObjectURL(new Blob([res.data]));
    const a = document.createElement("a"); a.href = u; a.download = name; a.click();
    URL.revokeObjectURL(u);
  };

  const totalShown = expenses.reduce((s, e) => s + (e.amount_inr || 0), 0);

  return (
    <div className="space-y-6 animate-fade-up">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="label-mono">CASHFLOW · OUTGOING</div>
          <h1 className="font-heading text-4xl font-black tracking-tight">Expenses</h1>
          <p className="text-sm text-zinc-600 mt-1 font-mono">Rent · Bills · Stationary · Travel — all in INR</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <input ref={fileRef} type="file" accept=".xlsx,.csv" onChange={onImport} className="hidden" data-testid="expense-import-input" />
          <button onClick={() => fileRef.current?.click()} className="brut-btn-secondary px-3 py-2 text-xs flex items-center gap-1" data-testid="expense-import-btn">
            <Upload className="w-3.5 h-3.5" /> IMPORT
          </button>
          <button onClick={() => downloadFile("/expenses/template", "expenses_template.xlsx")} className="brut-btn-secondary px-3 py-2 text-xs flex items-center gap-1" data-testid="expense-template-btn">
            <FileSpreadsheet className="w-3.5 h-3.5" /> TEMPLATE
          </button>
          <button onClick={() => setShowCreate(true)} className="brut-btn px-4 py-2 text-sm flex items-center gap-2" data-testid="add-expense-btn">
            <Plus className="w-4 h-4" /> NEW EXPENSE
          </button>
        </div>
      </div>

      {/* Monthly stats */}
      <div className="brut-card p-5">
        <div className="flex items-end justify-between mb-3 flex-wrap gap-2">
          <div>
            <div className="label-mono">EXPENSES THIS MONTH · {stats.month}</div>
            <div className="font-heading text-3xl font-black mt-1">₹ {stats.total_inr?.toLocaleString("en-IN", { maximumFractionDigits: 2 })}</div>
          </div>
        </div>
        {stats.categories?.length > 0 && (
          <div className="space-y-1 mt-3">
            {stats.categories.slice(0, 8).map((c) => {
              const pct = stats.total_inr > 0 ? (c.total_inr / stats.total_inr) * 100 : 0;
              return (
                <div key={c.category} className="flex items-center gap-2 text-xs">
                  <span className={`w-28 truncate font-mono font-bold px-2 py-0.5 border border-zinc-900 ${CATEGORY_COLORS[c.category] || "bg-zinc-100"}`}>
                    {CATEGORY_LABELS[c.category] || c.category}
                  </span>
                  <div className="flex-1 h-3 bg-zinc-100 border border-zinc-900 relative">
                    <div className="absolute inset-y-0 left-0 bg-zinc-900" style={{ width: `${pct}%` }} />
                  </div>
                  <span className="font-mono w-32 text-right">₹{c.total_inr.toLocaleString("en-IN")} <span className="text-zinc-500">· {c.count}</span></span>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Filters */}
      <div className="brut-card p-3 grid grid-cols-1 md:grid-cols-5 gap-3">
        <label className="block">
          <div className="label-mono mb-1">SEARCH</div>
          <div className="relative">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-zinc-500" />
            <input value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })} className="brut-input pl-7 pr-2 py-1.5 text-sm font-mono w-full" placeholder="description, vendor…" data-testid="expense-search-input" />
          </div>
        </label>
        <label className="block">
          <div className="label-mono mb-1">CATEGORY</div>
          <select value={filters.category} onChange={(e) => setFilters({ ...filters, category: e.target.value })} className="brut-input px-2 py-1.5 text-xs font-mono w-full">
            <option value="">All</option>
            {Object.entries(CATEGORY_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </label>
        <label className="block">
          <div className="label-mono mb-1">FROM</div>
          <input type="date" value={filters.start} onChange={(e) => setFilters({ ...filters, start: e.target.value })} className="brut-input px-2 py-1.5 text-xs font-mono w-full" />
        </label>
        <label className="block">
          <div className="label-mono mb-1">TO</div>
          <input type="date" value={filters.end} onChange={(e) => setFilters({ ...filters, end: e.target.value })} className="brut-input px-2 py-1.5 text-xs font-mono w-full" />
        </label>
        <div className="flex items-end">
          <div className="text-xs font-mono text-zinc-500">
            <div className="label-mono">SHOWING</div>
            <div className="font-bold text-sm text-zinc-900">₹{totalShown.toLocaleString("en-IN")}</div>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="brut-card overflow-x-auto">
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="bg-zinc-900 text-white">
              <Th>Date</Th><Th>Category</Th><Th>Description</Th><Th>Vendor</Th><Th>Payment</Th><Th>Reference</Th><Th align="right">Amount (INR)</Th><Th></Th>
            </tr>
          </thead>
          <tbody>
            {expenses.length === 0 && (
              <tr><td colSpan={8} className="text-center py-10 text-zinc-400 font-mono text-xs">NO EXPENSES YET — ADD ONE OR IMPORT EXCEL</td></tr>
            )}
            {expenses.map((e) => (
              <tr key={e.id} className="border-b border-zinc-200 hover:bg-zinc-50" data-testid={`expense-row-${e.id}`}>
                <Td><span className="font-mono">{e.date}</span></Td>
                <Td>
                  <span className={`font-mono text-[10px] uppercase font-bold px-2 py-0.5 border border-zinc-900 ${CATEGORY_COLORS[e.category] || "bg-zinc-100"}`}>{CATEGORY_LABELS[e.category]}</span>
                </Td>
                <Td className="font-bold">{e.description}</Td>
                <Td><span className="text-xs">{e.vendor || "—"}</span></Td>
                <Td><span className="font-mono text-xs uppercase">{e.payment_method || "—"}</span></Td>
                <Td><span className="font-mono text-xs">{e.reference || "—"}</span></Td>
                <Td align="right"><span className="font-mono font-bold">₹{e.amount_inr?.toLocaleString("en-IN")}</span></Td>
                <Td>
                  <div className="flex gap-1">
                    <button onClick={() => setEditing(e)} className="brut-btn-secondary p-1.5" data-testid={`edit-expense-${e.id}`}><Pencil className="w-3.5 h-3.5" /></button>
                    <button onClick={() => remove(e.id)} className="text-red-600 p-1.5" data-testid={`delete-expense-${e.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {(showCreate || editing) && (
        <ExpenseModal
          expense={editing}
          onClose={() => { setShowCreate(false); setEditing(null); }}
          onSaved={() => { setShowCreate(false); setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function ExpenseModal({ expense, onClose, onSaved }) {
  const [form, setForm] = useState(() => expense || {
    date: new Date().toISOString().slice(0, 10),
    category: "other", description: "", amount_inr: 0,
    vendor: "", payment_method: "bank", reference: "", notes: ""
  });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (expense) await api.patch(`/expenses/${expense.id}`, form);
      else await api.post("/expenses", form);
      toast.success(expense ? "Updated" : "Created");
      onSaved();
    } catch (err) { toast.error(err.response?.data?.detail || "Save failed"); }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 bg-zinc-900/40 z-50 flex items-center justify-center p-2 sm:p-4" onClick={onClose}>
      <div className="brut-card max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-heading text-2xl font-black">{expense ? "Edit Expense" : "New Expense"}</h2>
          <button onClick={onClose} className="brut-btn-secondary p-1.5"><X className="w-4 h-4" /></button>
        </div>
        <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label="DATE" required>
            <input required type="date" className="brut-input w-full px-3 py-2 text-sm font-mono" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
          </Field>
          <Field label="CATEGORY" required>
            <select required className="brut-input w-full px-3 py-2 text-sm" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="expense-category-select">
              {Object.entries(CATEGORY_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </Field>
          <Field label="DESCRIPTION" required full>
            <input required className="brut-input w-full px-3 py-2 text-sm" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="expense-description-input" />
          </Field>
          <Field label="AMOUNT (INR)" required>
            <input required type="number" step="0.01" className="brut-input w-full px-3 py-2 text-sm font-mono" value={form.amount_inr} onChange={(e) => setForm({ ...form, amount_inr: parseFloat(e.target.value) || 0 })} data-testid="expense-amount-input" />
          </Field>
          <Field label="PAYMENT METHOD">
            <select className="brut-input w-full px-3 py-2 text-sm font-mono" value={form.payment_method} onChange={(e) => setForm({ ...form, payment_method: e.target.value })}>
              <option value="bank">Bank Transfer</option>
              <option value="upi">UPI</option>
              <option value="cash">Cash</option>
              <option value="card">Card</option>
              <option value="cheque">Cheque</option>
            </select>
          </Field>
          <Field label="VENDOR / PAYEE">
            <input className="brut-input w-full px-3 py-2 text-sm" value={form.vendor} onChange={(e) => setForm({ ...form, vendor: e.target.value })} />
          </Field>
          <Field label="REFERENCE / BILL #">
            <input className="brut-input w-full px-3 py-2 text-sm font-mono" value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} />
          </Field>
          <Field label="NOTES" full>
            <textarea rows={2} className="brut-input w-full px-3 py-2 text-sm" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          </Field>
          <div className="md:col-span-2 flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="brut-btn-secondary px-4 py-2 text-sm">CANCEL</button>
            <button type="submit" disabled={saving} className="brut-btn px-4 py-2 text-sm disabled:opacity-50" data-testid="save-expense-btn">{saving ? "SAVING..." : (expense ? "SAVE" : "ADD EXPENSE")}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

const Th = ({ children, align = "left" }) => <th className={`text-${align} px-3 py-2 label-mono text-white border-b border-zinc-900 whitespace-nowrap`}>{children}</th>;
const Td = ({ children, align = "left", className = "" }) => <td className={`text-${align} px-3 py-2.5 ${className}`}>{children}</td>;
const Field = ({ label, children, full, required }) => (
  <label className={`block ${full ? "md:col-span-2" : ""}`}>
    <div className="label-mono mb-1">{label}{required && <span className="text-red-600"> *</span>}</div>
    {children}
  </label>
);
